/**
 * Customer detail — one person's record: who they are, what the SIS knows, and
 * everything they have done, in their own time zone.
 *
 * Route `customers.detail`, URL `/customers/<customerId>`. It survives a direct
 * navigation, a refresh and browser back/forward because the id is in the path,
 * not in a component's memory.
 *
 * Read-only except the customer-type assignment. Sections, in order: identity,
 * SIS-synchronised profile, appointments, events and registrations, walk-in
 * queue visits, messaging consent.
 *
 * The three history sections are one read, split by `department`. They are
 * hidden — not emptied — without `customers.service_record.read`, and a 403 on
 * the read hides them the same way: an operator who may not see a history
 * should not be told how empty it is.
 *
 * Data: partial — `getCustomer` (GET /v1/customers/{id}) OBSERVED 200 on
 * 2026-09-19 with `data.sis.holds[].title/description` resolved from the live
 * One-Stop identity-panel copy (JAI-1782). The holds alert and the
 * Synchronised-profile holds list read that field, but their RENDERING is
 * unverified: every SIS-bearing row in the mock world sits in a different
 * company from the one the app runs as, so no reachable
 * customer has a hold to draw. Seed ask is in
 * cc-outbox/2026-09-19-jai-1782-staff-hold-copy-ack.md.
 * `getCustomerServiceRecord`, `getAdminCustomerMessagingPreferences` and
 * `getCustomerListFieldCatalog` remain unobserved against the kit.
 *
 * 2026-09-28: the Synchronised profile reads only the JAI-1894 served view
 * (`sis.programs[].programEntry`, `attributes[]`, `sourceSystem`, `sync`); the
 * legacy flat keys and the `sisAttributes` seed fallback are gone. CC reports
 * the mocks serve that shape (addendum 2026-09-28); this project's copy of
 * packages/api-contracts predates it, and no browser has run against it from
 * here — acceptance is on Capture.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Btn, Icon, PageState, T } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { useNav } from "../../shell/router";
import { can } from "../../shell/access";
import { useIsMobile, useViewport } from "../../shell/useResponsive";
import { SectionRail, SectionHeaderBar, BackBox, type SectionRailGroup } from "../shared/section-rail";
import { currentLocale, useT } from "../../lib/i18n";
import { moduleKnownAbsent, useTenantModules } from "../../lib/tenant-modules";
import { friendlyDateShort, friendlyDateTime, friendlyTimeRange } from "../../lib/datetime";
import { useCustomerTypeCatalog } from "./customer-types";
import { CustomerTypeCard } from "./CustomerTypeCard";
import CustomerMessagingConsent from "./CustomerMessagingConsent";
import {
  CUSTOMER_OPS, SERVICE_RECORD_LIMIT_MAX, continuationBound, getCustomer, getCustomerServiceRecord, scopeProbe,
  useScopedRead, type ServiceRecordDepartment, type ServiceRecordEntry,
} from "./customers-data";
import { EM_DASH, humaniseFieldKey, projectSisProfile } from "./customer-columns";
import { DetailSection, Facts, Kpi, LifecycleChip, ReadError, useCustomerNoun, useIcu, initialsOf } from "./customers-shared";

/** Fallbacks for the attribute NAMES the server sends as `key`; the value is its own served `label`. */
const SIS_ATTR_FALLBACK: Record<string, string> = { level: "Level", classification: "Class year" };
const camelKey = (k: string): string => k.replace(/[_-]+([a-z0-9])/gi, (_, c: string) => c.toUpperCase());
/** A civil date (YYYY-MM-DD) is a calendar day, not an instant: format it at UTC noon in UTC so no zone shifts it. */
const civilDate = (d: string): string => /^\d{4}-\d{2}-\d{2}$/.test(d) ? friendlyDateShort(`${d}T12:00:00Z`, "UTC") : friendlyDateShort(d);
function relativeWhen(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  try {
    const rtf = new Intl.RelativeTimeFormat(currentLocale(), { numeric: "auto" });
    if (Math.abs(mins) < 60) return rtf.format(-mins, "minute");
    const hours = Math.round(mins / 60);
    if (Math.abs(hours) < 24) return rtf.format(-hours, "hour");
    const days = Math.round(hours / 24);
    if (Math.abs(days) < 30) return rtf.format(-days, "day");
  } catch { /* fall through to an absolute date */ }
  return friendlyDateShort(d);
}

export default function CustomerDetail() {
  const t = useT();
  const icu = useIcu();
  const nav = useNav();
  const { noun, nounPlural } = useCustomerNoun();
  const customerId = typeof nav.params.customerId === "string" ? nav.params.customerId : "";
  // Two columns once there is room for them: identity + synchronised profile on
  // the left, the activity history and consent on the right. Below that the
  // same sections stack in the drop's order.
  const twoUp = !useIsMobile(1100);
  const phone = useIsMobile(640);
  /* Desktop: Edit Event's layout (2026-10-05) — header bar, a left rail of sections, one section
     at a time. Same section list and order as the event roles' guest profile. Below desktop the
     record stacks exactly as before. */
  const desktop = useViewport() === "desktop";
  const [section, setSection] = useState<"overview" | "synced" | "appointments" | "events" | "queues" | "consent">("overview");
  const catalog = useCustomerTypeCatalog(can("customer_types.read"));

  /* JAI-2883: getCustomer is served only with the "reporting" module. When the module list is KNOWN and
     lacks it, nothing is requested and the screen says so; unknown keeps today's read. */
  const profilesAbsent = moduleKnownAbsent("reporting", useTenantModules());
  const identityRead = useScopedRead(`customer|${customerId}`, () => getCustomer(customerId), !!customerId && !profilesAbsent);
  const identity = identityRead.data?.data ?? null;
  const redirectTo = identityRead.data?.redirectTo ?? null;

  /** Point reread for the type card: by id, never a directory scan. */
  const rereadType = useCallback(async (): Promise<string | null | undefined> => {
    const inScope = scopeProbe();
    try {
      const res = await getCustomer(customerId);
      if (!inScope()) return undefined;
      identityRead.reload();
      return res.data.customerTypeId ?? undefined;
    } catch { return undefined; }
  }, [customerId, identityRead]);

  const back = (
    <span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }} onClick={() => nav.go("customers")}>
      <Icon.arrow dir="left" size={11} /> {icu("customers.detail.back", "All {nounPlural}", { nounPlural })}
    </span>
  );

  if (profilesAbsent) {
    return (
      <Screen rail="customers">
        <PageHeader title={t("customers.detail.unavailable.title", "Customer profiles are not part of this workspace")} breadcrumb={back} />
        <div style={{ padding: "14px 24px 48px" }} data-testid="customer-profile-unavailable">
          <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 8, padding: 24, display: "flex", flexDirection: "column", gap: 12, alignItems: "flex-start" }}>
            <p style={{ margin: 0, fontSize: 13.5, color: T.n2 }}>{icu("customers.detail.unavailable.body", "This workspace lists {nounPlural} but does not include their profiles.", { nounPlural })}</p>
            <Btn variant="secondary" size="sm" onClick={() => nav.go("customers")}>
              {icu("customers.detail.notFound.back", "Back to {nounPlural}", { nounPlural })}
            </Btn>
          </div>
        </div>
      </Screen>
    );
  }

  // No id in the path (`/customers/detail` typed by hand) is a not-found, not a
  // blank screen waiting for a read that will never be issued.
  if (!customerId || identityRead.failure?.notFound) {
    return (
      <Screen rail="customers">
        <PageHeader title={icu("customers.detail.notFound.title", "{noun} not found", { noun })} breadcrumb={back} />
        <div style={{ padding: "14px 24px 48px" }}>
          <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 8, padding: 24, display: "flex", flexDirection: "column", gap: 12, alignItems: "flex-start" }}>
            <p style={{ margin: 0, fontSize: 13.5, color: T.n2 }}>{t("customers.detail.notFound.body", "This record may have been merged or removed.")}</p>
            <Btn variant="secondary" size="sm" onClick={() => nav.go("customers")}>
              {icu("customers.detail.notFound.back", "Back to {nounPlural}", { nounPlural })}
            </Btn>
          </div>
        </div>
      </Screen>
    );
  }

  const displayName = identity?.name?.displayName
    || [identity?.name?.firstName, identity?.name?.lastName].filter(Boolean).join(" ")
    || (identity ? identity.customerId : "");

  const externalIds = identity?.externalIds;
  /* The SERVED view only (`sis`, JAI-1782/JAI-1894). */
  const sisSource = (identity?.sis ?? null) as Record<string, unknown> | null;
  const sisSupplied = identity ? identity.sis !== undefined : false;
  const sis = projectSisProfile(sisSource);
  /* The blocks that ACTUALLY render (addendum items 2–5). A program keeps its
     facts even without a served title — the line then says "Untitled program"
     so a Status row is never left without the program it belongs to. The code
     is shown beside the title only when it adds something (`code` is already
     null when the title IS the code — see projectSisProfile). */
  const programBlocks = sis.programs.map((p) => {
    const facts = [
      { label: t("customers.detail.sis.programStatus", "Status"), value: p.status ? t(`customers.detail.sis.enrollment.${camelKey(p.status)}`, humaniseFieldKey(p.status)) : null },
      { label: t("customers.detail.sis.startOn", "Started"), value: p.startOn ? civilDate(p.startOn) : null },
      { label: t("customers.detail.sis.expectedGraduation", "Expected graduation"), value: p.expectedGraduationDate ? civilDate(p.expectedGraduationDate) : null },
      { label: p.disciplines.length > 1 ? t("customers.detail.sis.disciplines", "Disciplines") : t("customers.detail.sis.discipline", "Discipline"), value: p.disciplines.length ? p.disciplines.join(", ") : null },
    ].filter((r) => r.value);
    if (!p.title && !facts.length) return null;
    const programValue = p.title
      ? [p.title, p.code].filter(Boolean).join(" \u00b7 ")
      : <span style={{ color: T.soft }}>{t("customers.detail.sis.programUntitled", "Untitled program")}</span>;
    return [{ label: t("customers.detail.sis.program", "Program"), value: programValue }, ...facts];
  }).filter((rows): rows is NonNullable<typeof rows> => !!rows);
  const sisBlocks: Array<"programs" | "attributes" | "holds"> = [
    ...(programBlocks.length ? ["programs" as const] : []),
    ...(sis.attributes.length ? ["attributes" as const] : []),
    ...(sis.holds.length ? ["holds" as const] : []),
  ];
  const sisDegraded = !!sis.sync?.degraded;
  const sisFooter = [
    sis.sourceName ? icu("customers.detail.sis.footerFrom", "From {source}", { source: sis.sourceName }) : null,
    sis.sync?.lastSyncedAt ? icu("customers.detail.sis.footerSynced", "synced {when}", { when: relativeWhen(sis.sync.lastSyncedAt) }) : null,
    sis.sync?.dataChangedAt ? icu("customers.detail.sis.footerChanged", "data changed {when}", { when: friendlyDateShort(sis.sync.dataChangedAt) }) : null,
  ].filter((x): x is string => !!x);
  /* Facts BESIDE the resolved copy, never instead of it: the source system's own
     spelling (staff need it for an SIS lookup) and the dates the hold covers. */
  const holdMeta = (h: { categoryRaw?: string; detail?: string; startOn?: string; endOn?: string }) => [
    h.categoryRaw ?? null,
    h.detail ?? null,
    h.startOn ? `${t("customers.detail.hold.since", "since")} ${friendlyDateShort(h.startOn)}` : null,
    h.endOn ? `${t("customers.detail.hold.until", "until")} ${friendlyDateShort(h.endOn)}` : null,
  ].filter((x): x is string => !!x);
  const historyAllowed = can("customers.service_record.read");
  const bookFor = (redirectTo ? redirectTo.split("/").pop() : "") || identity?.customerId || customerId;
  const SECTION_LABELS: Record<typeof section, string> = {
    overview: t("customers.detail.section.overview", "Overview"),
    synced: t("customers.detail.sis.title", "Synchronised profile"),
    appointments: t("customers.detail.appointments.title", "Appointments"),
    events: t("customers.detail.events.title", "Events & registrations"),
    queues: t("customers.detail.queues.title", "Walk-in queue visits"),
    consent: t("customers.detail.section.consent", "Messaging consent"),
  };
  const srow = (id: typeof section) => ({ id, label: SECTION_LABELS[id], active: section === id, onClick: () => setSection(id) });
  const sectionGroups: SectionRailGroup[] = [
    { id: "profile", heading: t("customers.detail.section.profile", "Profile"), role: "tablist", rows: [srow("overview"), srow("synced")] },
    /* Hidden — not emptied — without the history capability, as the stacked page does. */
    { id: "activity", heading: t("customers.detail.section.activity", "Activity"), role: "tablist", rows: historyAllowed ? [srow("appointments"), srow("events"), srow("queues")] : [] },
    { id: "details", heading: t("customers.detail.section.details", "Details"), role: "tablist", rows: [srow("consent")] },
  ];
  const show = (id: typeof section) => !desktop || section === id;
  const bookActions = identity && can("myschedule.read")
    ? ([["booking", t("customers.detail.book.appointment", "Appointment")], ["event", t("customers.detail.book.event", "Event")], ["queue", t("customers.detail.book.queue", "Queue")]] as [string, string][])
    : [];

  return (
    <Screen rail="customers">
      {desktop ? (
        <SectionHeaderBar
          lead={<BackBox label={icu("customers.detail.back", "All {nounPlural}", { nounPlural })} onClick={() => nav.go("customers")} />}
          title={displayName || icu("customers.detail.loading", "Loading {noun}\u2026", { noun: noun.toLowerCase() })}
          sub={identity ? SECTION_LABELS[section] : undefined}
          actions={bookActions.length ? bookActions.map(([what, label], i) => (
            <Btn key={what} variant={i === 0 ? undefined : "secondary"} onClick={() => nav.go("workforce.myday", { create: what, who: displayName, customerId: bookFor })}>{label}</Btn>
          )) : null}
        />
      ) : (
        <PageHeader title={displayName || icu("customers.detail.loading", "Loading {noun}\u2026", { noun: noun.toLowerCase() })} breadcrumb={back} />
      )}
      <DataSourceStrip screen="customers" ops={CUSTOMER_OPS} reqId="REQ-069" />
      <div style={desktop ? { flex: 1, minHeight: 0, display: "flex", overflow: "hidden" } : { display: "contents" }}>
      {desktop && identity ? <SectionRail ariaLabel={t("customers.detail.section.aria", "Record sections")} groups={sectionGroups} /> : null}
      <div style={desktop ? { flex: 1, minWidth: 0, overflow: "auto", display: "flex", flexDirection: "column" } : { display: "contents" }}>

      {/* Holds come first: a hold blocks booking, so an advisor must see it
          before the actions below it. ONE ALERT GROUP for every active hold —
          the wording is the server's resolved copy (JAI-1782), the same words
          the student reads on the One-Stop identity panel, so staff and student
          never name the same hold differently. The bursar-specific banner this
          replaces matched on a `code` field the canonical read never sends. */}
      {identity && sis.holds.length ? (
        <div role="alert" style={{ margin: phone ? "10px 12px 0" : "10px 24px 0", padding: "12px 14px", borderRadius: 10, background: T.red2, border: `1px solid ${T.red1}`, display: "flex", gap: 12, alignItems: "flex-start" }}>
          <span style={{ display: "inline-flex", color: T.red1, marginTop: 2 }}><Icon.warn size={16} /></span>
          <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>
              {sis.holds.length > 1
                ? icu("customers.detail.hold.titleCount", "{count} holds block booking", { count: sis.holds.length })
                : t("customers.detail.hold.title", "A hold blocks booking")}
            </div>
            {sis.holds.map((h, i) => (
              <div key={`${h.category}-${i}`} style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: T.n1 }}>{h.title}</div>
                {h.description ? (
                  <div style={{ fontSize: 13, color: T.n1, lineHeight: 1.45, marginTop: 2 }}>{h.description}</div>
                ) : null}
                {holdMeta(h).length ? (
                  <div style={{ fontSize: 12.5, color: T.n2, marginTop: 4 }}>{holdMeta(h).join(" \u00b7 ")}</div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* Book this student in — the same three forms My Day's + buttons open,
          with the student already chosen. Full-width row on a phone. The route
          carries the record's canonical id beside the name, so the form opens on
          this record instead of a name typed in by hand; a merged record books
          the record it was merged into (`redirectTo`). */}
      {identity && can("myschedule.read") && !desktop ? (
        <div style={{ padding: phone ? "10px 12px 0" : "10px 24px 0", display: "flex", gap: 8, flexWrap: "nowrap" }}>
          {([["booking", t("customers.detail.book.appointment", "Appointment")], ["event", t("customers.detail.book.event", "Event")], ["queue", t("customers.detail.book.queue", "Queue")]] as [string, string][]).map(([what, label]) => (
            <button key={what} type="button" onClick={() => nav.go("workforce.myday", { create: what, who: displayName, customerId: bookFor })}
              style={{ flex: phone ? "1 1 0" : "0 0 auto", minWidth: 0, height: 44, padding: phone ? "0 8px" : "0 18px", borderRadius: 6, border: "none", cursor: "pointer",
                background: T.p1, color: T.onColor, fontFamily: T.font, fontSize: 13.5, fontWeight: 600, whiteSpace: "nowrap" }}>
              {label}
            </button>
          ))}
        </div>
      ) : null}

      <div style={{ padding: "14px 24px 48px", display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
        <PageState
          noun={noun.toLowerCase()}
          loading={identityRead.loading}
          error={identityRead.failure && !identityRead.failure.notFound
            ? (identityRead.failure.denied
              ? t("customers.detail.denied", "Your role can\u2019t open this record.")
              : identityRead.failure.message)
            : null}
          onRetry={identityRead.failure?.denied ? undefined : identityRead.reload}
          retryLabel={t("common.actions.retry", "Retry")}
          skeleton="table"
          skeletonRows={4}
        >
          {identity ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {identity.status === "merged" ? (
                <div role="status" style={{ display: "flex", gap: 10, alignItems: "center", padding: "10px 14px", borderRadius: 8, background: T.orange2, fontSize: 12.5, color: T.n1, flexWrap: "wrap" }}>
                  <span style={{ display: "inline-flex", color: T.orange1 }}><Icon.warn size={14} /></span>
                  <span style={{ flex: 1, minWidth: 200 }}>
                    {icu("customers.detail.identity.mergedInto", "Merged into {target}", { target: identity.mergedIntoCustomerId ?? EM_DASH })}
                  </span>
                  {redirectTo ? (
                    <Btn variant="secondary" size="sm" onClick={() => nav.go("customers.detail", { customerId: redirectTo.split("/").pop() ?? redirectTo })}>
                      {t("customers.detail.identity.openMerged", "Open current record")}
                    </Btn>
                  ) : null}
                </div>
              ) : null}

              {/* Identity and the synchronised profile sit side by side: two
                  read-only descriptions of the same person, equally weighted.
                  Activity and consent run full width beneath them. */}
              <div style={{ display: "grid", gridTemplateColumns: twoUp && !desktop ? "minmax(0, 1fr) minmax(0, 1fr)" : "minmax(0, 1fr)", gap: 16, alignItems: "start", minWidth: 0, maxWidth: desktop ? 880 : undefined }}>
              {/* 1 — identity */}
              {show("overview") ? <DetailSection title={t("customers.detail.identity.title", "Identity")}>
                <div style={{ display: "flex", gap: 12, alignItems: "center", padding: phone ? "12px 16px" : "14px 16px", borderBottom: `1px solid ${T.n6}` }}>
                  <span style={{
                    width: 44, height: 44, borderRadius: 999, background: T.p1, color: T.onColor,
                    display: "grid", placeItems: "center", fontSize: 15, fontWeight: 600, flexShrink: 0,
                  }}>{initialsOf(displayName)}</span>
                  <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                    <span style={{ fontSize: 16, fontWeight: 700, color: T.n1 }}>{displayName}</span>
                    <span style={{ fontFamily: T.fontMono, fontSize: 11, color: T.soft }}>{identity.customerId}</span>
                  </span>
                </div>
                <Facts rows={[
                  {
                    label: t("customers.detail.identity.status", "Status"),
                    value: t(`customers.detail.status.${identity.status}`, humaniseFieldKey(identity.status)),
                  },
                  {
                    label: t("customers.detail.identity.externalIds", "External IDs"),
                    value: externalIds === undefined
                      ? <span style={{ color: T.soft }}>{t("customers.detail.identity.externalIdsPending", "Not available on this record yet.")}</span>
                      : externalIds.length
                        ? externalIds.map((x) => `${x.system.toUpperCase()} ${x.value}`).join(" \u00b7 ")
                        : null,
                  },
                  {
                    label: t("customers.detail.identity.emails", "Email addresses"),
                    value: identity.emails?.length
                      ? <ContactList items={identity.emails.map((e) => ({ value: e.value, meta: [e.purpose, e.verification].filter(Boolean).join(" \u00b7 ") }))} />
                      : <span style={{ color: T.soft }}>{t("customers.detail.identity.noEmails", "No email address on file.")}</span>,
                  },
                  {
                    label: t("customers.detail.identity.phones", "Phone numbers"),
                    value: identity.phones?.length
                      ? <ContactList items={identity.phones.map((p) => ({ value: p.value, meta: [p.purpose, p.verification].filter(Boolean).join(" \u00b7 ") }))} />
                      : <span style={{ color: T.soft }}>{t("customers.detail.identity.noPhones", "No phone number on file.")}</span>,
                  },
                  {
                    label: t("customers.detail.identity.address", "Address"),
                    value: identity.postalAddresses?.length
                      ? <ContactList items={identity.postalAddresses.map((a) => ({
                        value: [a.addressLine1, a.addressLine2, a.city, a.postcode, a.country].filter(Boolean).join(", "),
                        meta: a.purpose ?? "",
                      }))} />
                      : <span style={{ color: T.soft }}>{t("customers.detail.identity.noAddress", "No address on file.")}</span>,
                  },
                  {
                    label: t("customers.detail.identity.localeTimezone", "Language & time zone"),
                    value: [identity.locale, identity.timezone].filter(Boolean).join(" \u00b7 ") || null,
                  },
                ]} />
                <div style={{ borderTop: `1px solid ${T.n6}` }}>
                  {identity.customerTypeId === undefined ? (
                    <p style={{ margin: 0, padding: "14px 16px", fontSize: 12.5, color: T.soft }}>
                      {t("customers.type.pending", "The type assignment isn\u2019t available on this record yet, so it can\u2019t be shown or changed here.")}
                    </p>
                  ) : (
                    <CustomerTypeCard
                      customerId={identity.customerId}
                      currentTypeId={identity.customerTypeId ?? null}
                      catalog={catalog}
                      onRefetch={rereadType}
                    />
                  )}
                </div>
              </DetailSection> : null}

              {/* 2 — SIS-synchronised profile */}
              {show("synced") ? <DetailSection title={t("customers.detail.sis.title", "Synchronised profile")}>
                {!sisSupplied ? (
                  <p style={{ margin: 0, padding: "14px 16px", fontSize: 12.5, color: T.soft }}>
                    {t("customers.detail.sis.pending", "Synchronised profile data isn\u2019t available on this record yet.")}
                  </p>
                ) : !sisBlocks.length && !sisDegraded ? (
                  <p style={{ margin: 0, padding: "14px 16px", fontSize: 12.5, color: T.soft }}>
                    {t("customers.detail.sis.empty", "Nothing is synchronised for this record.")}
                  </p>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    {sis.sync?.degraded ? (
                      <div role="status" style={{ margin: "12px 16px 0", padding: "8px 10px", borderRadius: 6, background: T.orange2, fontSize: 12.5, color: T.n1, display: "flex", gap: 8, alignItems: "center" }}>
                        <span style={{ display: "inline-flex", color: T.orange1 }}><Icon.warn size={14} /></span>
                        <span style={{ minWidth: 0 }}>{t("customers.detail.sis.degraded", "Source unreachable \u2014 showing last good data")}</span>
                      </div>
                    ) : null}
                    {!sisBlocks.length ? (
                      <p style={{ margin: 0, padding: "14px 16px", fontSize: 12.5, color: T.soft }}>
                        {t("customers.detail.sis.empty", "Nothing is synchronised for this record.")}
                      </p>
                    ) : null}
                    {programBlocks.map((rows, i) => (
                      <div key={`program-${i}`} style={i ? { borderTop: `1px solid ${T.n6}` } : undefined}><Facts rows={rows} /></div>
                    ))}
                    {sis.attributes.length ? (
                      <div style={sisBlocks.indexOf("attributes") > 0 ? { borderTop: `1px solid ${T.n6}` } : undefined}>
                        <Facts rows={sis.attributes.map((a) => ({
                          label: t(`customers.detail.sis.attr.${camelKey(a.key)}`, SIS_ATTR_FALLBACK[a.key] ?? humaniseFieldKey(a.key)),
                          value: a.mapped ? a.value : (
                            <span style={{ color: T.soft }}>{a.value}{" \u00b7 "}{t("customers.detail.sis.unmapped", "unmapped")}</span>
                          ),
                        }))} />
                      </div>
                    ) : null}
                    {sis.holds.length ? (
                      <div style={{ padding: sisBlocks.indexOf("holds") > 0 ? "12px 16px 14px" : "0 16px 14px", borderTop: sisBlocks.indexOf("holds") > 0 ? `1px solid ${T.n6}` : undefined }}>
                        <span style={{ fontSize: 11.5, fontWeight: 700, color: T.n1, display: "block", marginBottom: 6 }}>{t("customers.detail.sis.holds", "Holds")}</span>
                        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
                          {sis.holds.map((h, i) => (
                            <li key={`${h.category}-${i}`} style={{ padding: "8px 10px", borderRadius: 6, background: T.orange2, fontSize: 12.5, color: T.n1 }}>
                              <span style={{ fontWeight: 600 }}>{h.title}</span>
                              {h.description ? <span style={{ display: "block", marginTop: 2 }}>{h.description}</span> : null}
                              {holdMeta(h).length ? (
                                <span style={{ display: "block", fontSize: 11.5, color: T.n2, marginTop: 2 }}>{holdMeta(h).join(" \u00b7 ")}</span>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                    {sisFooter.length ? (
                      <p style={{ margin: 0, padding: "10px 16px 14px", borderTop: `1px solid ${T.n6}`, fontSize: 11.5, color: T.n2, overflowWrap: "anywhere" }}>
                        {sisFooter.join(" \u00b7 ")}
                      </p>
                    ) : null}
                  </div>
                )}
              </DetailSection> : null}

              </div>

              {/* 3–5 — history (desktop: the KPI strip on Overview, one department per section) */}
              {historyAllowed && (!desktop || section !== "synced" && section !== "consent") ? (
                <div style={desktop ? { maxWidth: 880, display: "flex", flexDirection: "column", gap: 16, order: section === "overview" ? -1 : 0 } : { display: "contents" }}>
                  <History customerId={identity.customerId} only={!desktop ? undefined : section === "overview" ? "summary" : (section as ServiceRecordDepartment)} />
                </div>
              ) : null}

              {/* 6 — consent */}
              {show("consent") ? <div style={desktop ? { maxWidth: 880 } : { display: "contents" }}><CustomerMessagingConsent customerId={identity.customerId} /></div> : null}
            </div>
          ) : null}
        </PageState>
      </div>
      </div>
      </div>
    </Screen>
  );
}

function ContactList({ items }: { items: Array<{ value: string; meta: string }> }) {
  return (
    <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 4 }}>
      {items.map((x, i) => (
        <li key={`${x.value}-${i}`} style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, color: T.n1, overflowWrap: "anywhere" }}>{x.value}</span>
          {x.meta ? <span style={{ fontSize: 11, color: T.soft }}>{x.meta}</span> : null}
        </li>
      ))}
    </ul>
  );
}

const DEPARTMENTS: ServiceRecordDepartment[] = ["appointments", "events", "queues"];

/**
 * The three history sections. One read per page of history; continuation moves
 * the exclusive `to` bound to the oldest occurrence in hand.
 *
 * ⚠ That bound can skip entries sharing a page-boundary timestamp (CC dep 6).
 * Until lossless continuation ships, truncation is stated plainly and this UI
 * never claims the history is complete.
 */
function History({ customerId, only }: { customerId: string; /** Desktop sections (2026-10-05): just the KPI strip, or just one department. */ only?: "summary" | ServiceRecordDepartment }) {
  const t = useT();
  const { noun } = useCustomerNoun();
  const icu = useIcu();
  const [entries, setEntries] = useState<ServiceRecordEntry[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState<{ message: string; denied: boolean } | null>(null);
  const [nonce, setNonce] = useState(0);
  const gen = useRef(0);

  const load = useCallback(async (to?: string) => {
    gen.current += 1;
    const mine = gen.current;
    const inScope = scopeProbe();
    setLoading(true);
    try {
      const res = await getCustomerServiceRecord(customerId, { to, limit: SERVICE_RECORD_LIMIT_MAX });
      if (mine !== gen.current || !inScope()) return;
      setEntries((prev) => {
        const seen = new Set(prev.map((e) => e.entryId));
        return to ? [...prev, ...res.data.filter((e) => !seen.has(e.entryId))] : res.data;
      });
      setTruncated(!!res.meta?.truncated);
      setFailure(null);
    } catch (e) {
      if (mine !== gen.current || !inScope()) return;
      const status = (e as { status?: number })?.status ?? 0;
      const message = (e as { message?: string })?.message?.trim() || t("customers.detail.history.failed", "This history couldn\u2019t be read.");
      setFailure({ message, denied: status === 403 || status === 401 });
    } finally { if (mine === gen.current) setLoading(false); }
  }, [customerId, t]);

  useEffect(() => { setEntries([]); setTruncated(false); void load(); }, [customerId, nonce, load]);

  const grouped = useMemo(() => {
    const by: Record<ServiceRecordDepartment, ServiceRecordEntry[]> = { appointments: [], events: [], queues: [] };
    for (const e of entries) if (by[e.department]) by[e.department].push(e);
    for (const d of DEPARTMENTS) by[d].sort((a, b) => (a.occurredAt < b.occurredAt ? 1 : -1));
    return by;
  }, [entries]);

  // A denied history is HIDDEN, not emptied.
  if (failure?.denied) return null;

  const titles: Record<ServiceRecordDepartment, string> = {
    appointments: t("customers.detail.appointments.title", "Appointments"),
    events: t("customers.detail.events.title", "Events & registrations"),
    queues: t("customers.detail.queues.title", "Walk-in queue visits"),
  };
  const empties: Record<ServiceRecordDepartment, string> = {
    appointments: icu("customers.detail.appointments.empty", "No appointments recorded for this {noun} yet.", { noun: noun.toLowerCase() }),
    events: icu("customers.detail.events.empty", "No event registrations recorded for this {noun} yet.", { noun: noun.toLowerCase() }),
    queues: icu("customers.detail.queues.empty", "No queue visits recorded for this {noun} yet.", { noun: noun.toLowerCase() }),
  };

  return (
    <>
      {/* What this person has actually done, counted from the activity in hand.
          These are NOT directory totals: when the history is truncated the read
          holds only the most recent page, and the strip says so rather than
          presenting a partial count as the whole record. */}
      {failure || (only && only !== "summary") ? null : (
        /* order:-1 lifts the counts to the top of the record, under the name and
           the three book buttons, ahead of identity — the parent is a column. */
        <div style={{ display: "flex", flexDirection: "column", gap: 6, order: -1 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
            <Kpi label={titles.appointments} value={loading && !entries.length ? EM_DASH : grouped.appointments.length} />
            <Kpi label={titles.events} value={loading && !entries.length ? EM_DASH : grouped.events.length} />
            <Kpi label={titles.queues} value={loading && !entries.length ? EM_DASH : grouped.queues.length} />
            <Kpi
              label={t("customers.detail.timeline.lastActivity", "Last activity")}
              value={entries.length ? friendlyDateTime(entries.reduce((a, e) => (e.occurredAt > a ? e.occurredAt : a), entries[0].occurredAt)) : EM_DASH}
            />
          </div>
          {truncated ? (
            <span style={{ fontSize: 11.5, color: T.soft }}>
              {t("customers.detail.timeline.countsPartial", "Counted from the activity loaded so far \u2014 earlier activity is not included yet.")}
            </span>
          ) : null}
        </div>
      )}

      {(only === "summary" ? [] : only ? [only] : DEPARTMENTS).map((department) => (
        <DetailSection key={department} title={titles[department]}>
          {failure ? (
            <ReadError message={failure.message} onRetry={() => setNonce((n) => n + 1)} retryLabel={t("common.actions.retry", "Retry")} />
          ) : loading && !entries.length ? (
            <p style={{ margin: 0, padding: "14px 16px", fontSize: 12.5, color: T.soft }}>{t("customers.detail.history.loading", "Loading activity\u2026")}</p>
          ) : grouped[department].length === 0 ? (
            <p style={{ margin: 0, padding: "14px 16px", fontSize: 12.5, color: T.soft, fontStyle: "italic" }}>{empties[department]}</p>
          ) : (
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {grouped[department].map((entry, i) => (
                <li key={entry.entryId} style={{ padding: "11px 16px", borderTop: i ? `1px solid ${T.n6}` : 0, display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
                  <span style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: T.n1 }}>
                      {entry.title || entry.containerTitle || <span style={{ color: T.soft }}>{EM_DASH}</span>}
                    </span>
                    <LifecycleChip state={entry.lifecycleState} />
                  </span>
                  <span style={{ fontSize: 11.5, color: T.soft }}>
                    {[
                      // An unscheduled walk-in still has an occurrence time; a
                      // planned window is shown ONLY when the entry carries one.
                      entry.plannedStartAt
                        ? `${friendlyDateTime(entry.plannedStartAt, entry.timezone ?? undefined)}${entry.plannedEndAt ? ` \u2014 ${friendlyTimeRange(entry.plannedStartAt, entry.plannedEndAt, entry.timezone ?? undefined).split(" \u2014 ")[1]}` : ""}`
                        : friendlyDateTime(entry.occurredAt, entry.timezone ?? undefined),
                      entry.staffName,
                      entry.locationName,
                      entry.containerTitle && entry.title ? entry.containerTitle : null,
                    ].filter(Boolean).join(" \u00b7 ")}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </DetailSection>
      ))}

      {truncated && !failure ? (
        <div style={{ display: "flex", gap: 10, alignItems: "center", padding: "10px 14px", borderRadius: 8, background: T.white, border: `1px solid ${T.border}`, flexWrap: "wrap" }}>
          <span style={{ flex: 1, minWidth: 200, fontSize: 12.5, color: T.n2 }}>
            {t("customers.detail.timeline.truncated", "Showing the most recent activity only.")}
          </span>
          <Btn
            variant="secondary"
            size="sm"
            disabled={loading}
            onClick={() => { const bound = continuationBound(entries); if (bound) void load(bound); }}
          >{t("customers.detail.timeline.showMore", "Show earlier activity")}</Btn>
        </div>
      ) : null}
    </>
  );
}
